import type maplibregl from 'maplibre-gl';
import type { StyleSpecification } from 'maplibre-gl';

export interface MapTheme {
  id: string;
  label: string;
  /** A Carto GL style URL, or an inline MapLibre style object (satellite). */
  style: string | StyleSpecification;
}

/** Esri World Imagery raster — keyless aerial photography (no glyphs → no labels). */
const SATELLITE_STYLE: StyleSpecification = {
  version: 8,
  sources: {
    'esri-imagery': {
      type: 'raster',
      tiles: [
        'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
      ],
      tileSize: 256,
      attribution: 'Imagery © Esri, Maxar, Earthstar Geographics',
    },
  },
  layers: [{ id: 'esri-imagery', type: 'raster', source: 'esri-imagery' }],
};

export const MAP_THEMES: MapTheme[] = [
  { id: 'dark', label: 'Dark', style: 'https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json' },
  { id: 'light', label: 'Light', style: 'https://basemaps.cartocdn.com/gl/positron-gl-style/style.json' },
  { id: 'voyager', label: 'Voyager', style: 'https://basemaps.cartocdn.com/gl/voyager-gl-style/style.json' },
  { id: 'satellite', label: 'Satellite', style: SATELLITE_STYLE },
];

export const DEFAULT_THEME_ID = 'dark';

/** Themes whose base map is light enough that the night ambient tint looks wrong. */
const LIGHT_THEME_IDS = new Set(['light', 'satellite']);

export function themeAllowsTint(themeId: string): boolean {
  return !LIGHT_THEME_IDS.has(themeId);
}

const STORAGE_KEY = 'vitals.theme';
const KNOWN_IDS = new Set(MAP_THEMES.map((t) => t.id));

export function loadThemeId(): string {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw !== null && KNOWN_IDS.has(raw) ? raw : DEFAULT_THEME_ID;
  } catch {
    return DEFAULT_THEME_ID;
  }
}

export function saveThemeId(id: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, id);
  } catch {
    // best-effort persistence
  }
}

export function findTheme(id: string): MapTheme {
  return MAP_THEMES.find((t) => t.id === id) ?? MAP_THEMES[0]!;
}

/** Tracks any pending idle handler per map instance to cancel on rapid re-swap. */
const pendingStyleLoad = new WeakMap<maplibregl.Map, () => void>();

/**
 * Swap the base map style. MapLibre's setStyle() drops everything attached to the
 * base map, so `onReady` (which re-applies the radar raster) MUST run after the new
 * style finishes loading. The deck.gl overlay is a separate canvas and is unaffected.
 *
 * A runtime setStyle() does NOT reliably emit `style.load` (that only fires for the
 * initial style), and the `styledata`/`isStyleLoaded` pair proved flaky across
 * repeated swaps. The `idle` event — emitted once the new style has loaded and the
 * map has settled — fires reliably on every swap, so we wait for the first idle
 * where the style is actually loaded (re-arming if an idle arrives mid-load). If
 * called again before it fires (rapid theme switching), the prior pending handler
 * is cancelled before registering the new one.
 */
export function applyTheme(map: maplibregl.Map, themeId: string, onReady: () => void): void {
  const prior = pendingStyleLoad.get(map);
  if (prior !== undefined) {
    map.off('idle', prior);
  }
  const handler = (): void => {
    if (!map.isStyleLoaded()) {
      map.once('idle', handler); // settled before the style finished; wait again
      return;
    }
    pendingStyleLoad.delete(map);
    onReady();
  };
  pendingStyleLoad.set(map, handler);
  const theme = findTheme(themeId);
  map.setStyle(theme.style);
  map.once('idle', handler);
}
